import { useEffect, useRef } from 'react';
import { useOnboardingContext } from '../../../contexts/OnboardingContext';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useDiagramStore } from '../../../stores/diagramStore';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { Logger } from '../../../utils/logger';

interface UseProcessDemoOnboardingOptions {
  enabled: boolean;
  canWrite: boolean;
  isLoadingAccess: boolean;
}

export const useProcessDemoOnboarding = ({
  enabled,
  canWrite,
  isLoadingAccess,
}: UseProcessDemoOnboardingOptions): void => {
  const { loadDemoData } = useOnboardingContext();
  const { isSynced } = useDiagramDocument();
  const nodeCount = useDiagramStore((s) => s.nodes.length);
  const didRunDemoImportRef = useRef(false);

  useEffect(() => {
    if (!enabled || !loadDemoData || didRunDemoImportRef.current) return;
    if (!isSynced || isLoadingAccess || !canWrite) return;
    if (nodeCount > 0) return;

    didRunDemoImportRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const [{ loadProcessDemoScene }, { BatchCreateNodesCommand }] = await Promise.all([
          import('../services/loadProcessDemoScene'),
          import('../../../core/commands/BatchCreateNodesCommand'),
        ]);

        const { nodes: demoNodes, edges: demoEdges } = await loadProcessDemoScene();
        if (cancelled) return;

        const cm = getCommandManager();
        await cm.execute(new BatchCreateNodesCommand(demoNodes, demoEdges));
        cm.clear();
      } catch (err) {
        Logger.getLoggerWithPrefix('ProcessDemoOnboarding').error('Failed to load process demo', err);
        didRunDemoImportRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, loadDemoData, isSynced, isLoadingAccess, canWrite, nodeCount]);
};
